// ============================================================
// useNotifications Hook
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getNotifications, markAllNotificationsAsRead } from '@/api/notifications';
import type { Notification, NotificationsResponse } from '@/api/notifications';
import { useAuthStore } from '@/stores/authStore';

export function useNotifications(limit = 5) {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  
  // Fetch notifications only if authenticated (top 5 by default for performance)
  const { data, isLoading, error, refetch } = useQuery<NotificationsResponse>({
    queryKey: ['notifications', limit],
    queryFn: () => getNotifications(limit),
    enabled: isAuthenticated, // Only fetch if user is authenticated
    refetchInterval: isAuthenticated ? 60000 : false, // Poll every 60s (was 30s)
    retry: false, // Don't retry on 401 errors
    staleTime: 30000, // Consider data fresh for 30 seconds
  });

  // Mark all as read mutation
  const markAllReadMutation = useMutation({
    mutationFn: markAllNotificationsAsRead,
    onSuccess: () => {
      // Invalidate and refetch notifications
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllAsRead = useCallback(() => {
    markAllReadMutation.mutate();
  }, [markAllReadMutation]);

  return {
    notifications: data?.notifications || [],
    unreadCount: data?.unread_count || 0,
    isLoading,
    error,
    markAllAsRead,
    refetch,
  };
}
